import React from 'react';
import { FormField, Label, Listbox } from '@zillow/constellation';

import type { Section } from '../../../../models/index';
import { PAYMENT_METHOD } from '../../../../constants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  onAddOne: () => void;
  onRemoveAll: () => void;
};

export default function PaymentMethodListBox({ section, setSectionValues, onRemoveAll, onAddOne }: Props): JSX.Element {
  function onOptionSelect(option: { id: string; value: string; label: string }): void {
    if (option.value === PAYMENT_METHOD.FIELD_NAME.ELECTRONIC_IS_ENABLED) {
      const electronicField = section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.ELECTRONIC_IS_ENABLED);

      electronicField ? onRemoveAll() : onAddOne();
    }

    setSectionValues({
      [option.value]: !section.getFieldValue(option.value),
    });
  }

  const totalPaymentMethods = section.getTotalIndexes();
  if (totalPaymentMethods == null) {
    return null;
  }

  const listboxValues = [];
  const zillowPayments = section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.ZILLOW_PAYMENTS_IS_ENABLED);
  const checkOrMoneyOrder = section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.CHECK_IS_ENABLED);
  const directDeposit = section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.DIRECT_IS_ENABLED);
  const otherElectronicPayment = section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.ELECTRONIC_IS_ENABLED);

  if (zillowPayments) {
    listboxValues.push(PAYMENT_METHOD.FIELD_NAME.ZILLOW_PAYMENTS_IS_ENABLED);
  }
  if (checkOrMoneyOrder) {
    listboxValues.push(PAYMENT_METHOD.FIELD_NAME.CHECK_IS_ENABLED);
  }
  if (directDeposit) {
    listboxValues.push(PAYMENT_METHOD.FIELD_NAME.DIRECT_IS_ENABLED);
  }
  if (otherElectronicPayment) {
    listboxValues.push(PAYMENT_METHOD.FIELD_NAME.ELECTRONIC_IS_ENABLED);
  }

  const isCheckOrMoneyOrderDisabled = section.isFieldDisabled(PAYMENT_METHOD.FIELD_NAME.CHECK_IS_ENABLED);

  return (
    <FormField
      label={<Label size="lg">Which form(s) of payment will you accept?</Label>}
      control={
        <Listbox
          value={listboxValues}
          size="fluid"
          onOptionSelect={onOptionSelect}
          options={[
            {
              value: PAYMENT_METHOD.FIELD_NAME.ZILLOW_PAYMENTS_IS_ENABLED,
              label: 'Zillow Payments',
              meta: 'Tenant pays through our online service. Set up quickly using information from your lease.',
            },
            {
              value: PAYMENT_METHOD.FIELD_NAME.CHECK_IS_ENABLED,
              label: 'Check or money order',
              meta: 'Tenant sends payment to either the landlord or the property manager.',
              disabled: isCheckOrMoneyOrderDisabled,
            },
            {
              value: PAYMENT_METHOD.FIELD_NAME.DIRECT_IS_ENABLED,
              label: 'Direct deposit',
              meta: 'Tenant transfers funds using bank info, including ACH, if you provide them.',
            },
            {
              value: PAYMENT_METHOD.FIELD_NAME.ELECTRONIC_IS_ENABLED,
              label: 'Other electronic payment',
              meta: 'Tenant pays via third-party online service of your choice.',
            },
          ]}
        />
      }
    />
  );
}
